export const INVESTOR_SEARCH_PATH = '/investor-search';

// The core gateway routes `/ps-core/investors/**` to investor-service
// (propstream-core `gateway/.../application.yml`), which also settles the
// `/ps-core/` vs `/ps/` question the S4 build-spec §6 raised.
export const INVESTOR_SEARCH_ENDPOINT = '/ps-core/investors/search';

// Who owns the searched property, and the property itself in the comparable
// shape — fetched alongside every search to fill the subject pin's hover card.
export const SUBJECT_PROPERTY_INVESTOR_ENDPOINT = '/ps-core/investors/subject-property-investor';

// This surface keeps its OWN recent-search list. Never write into the main
// search's `PropStream.RecentSuggestions.1` — clearing recents on one surface
// must not clear the other (S4 build-spec §2).
export const RECENT_SUBJECT_PATH = 'PropStream.InvestorRecentSuggestions.1';
export const MAX_RECENT_SUBJECTS = 15;

// Filter option lists and defaults come from the PRD ("What Filters Are
// Available in Investor Search?"). They are fixed enums held on the FE; the
// backend accepts the selected values and maps them to the vendor.
export const SearchRadiusOptions = [1, 2, 3, 5, 7].map((value) => ({
  value,
  label: `${value} ${value === 1 ? 'mile' : 'miles'}`,
}));

export const PurchaseTimeWindowOptions = [1, 2, 3, 5].map((value) => ({
  value,
  label: `${value} ${value === 1 ? 'year' : 'years'}`,
}));

export const PropertyTypeOptions = [
  { value: 'ANY', label: 'Any' },
  { value: 'RESIDENTIAL', label: 'Residential' },
  { value: 'COMMERCIAL', label: 'Commercial' },
  { value: 'VACANT_LAND', label: 'Vacant Land' },
];

// Verbatim from the PRD. Note the fifth entry is printed there on a single line
// as "IBUYER, REAL ESTATE INVESTMENT AND DEVELOPMENT" — modelled as ONE option
// until product confirms whether it is one or two (S4 build-spec §6). The exact
// vendor tokens are still to be confirmed with the S2 owner.
export const InvestorTypeOptions = [
  { value: 'LANDLORD', label: 'Landlord' },
  { value: 'FLIPPER', label: 'Flipper' },
  { value: 'CASH BUYER', label: 'Cash Buyer' },
  { value: 'HOME BUILDER', label: 'Home Builder' },
  { value: 'LAND BANK', label: 'Land Bank' },
  { value: 'COMMERCIAL LAND OWNER', label: 'Commercial Land Owner' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'TITLE COMPANY', label: 'Title Company' },
  { value: 'FINANCING', label: 'Financing' },
  { value: 'UTILITY COMPANY', label: 'Utility Company' },
];

// What the Investor Type control actually renders (Figma 2621:73418). Its "Any"
// row is NOT an eleventh investor type: it stands for the empty selection, which
// is how the designer's rule works in both directions — picking Any clears every
// specific type, and picking a type leaves the list non-empty so Any deselects
// itself. `exclusive` is what `SearchFilter/Dropdown` keys that behaviour off.
//
// It is deliberately a separate list from `InvestorTypeOptions`, which stays the
// vendor vocabulary: `investorUtils` expands an empty selection to exactly those
// ten values, and a sentinel must never reach the request.
export const ANY_INVESTOR_TYPE = 'ANY';

export const InvestorTypeFilterOptions = [
  { value: ANY_INVESTOR_TYPE, label: 'Any', exclusive: true },
  ...InvestorTypeOptions,
];

// The PRD defines the meaning but not the option list; these come from the
// Investor Search analysis page (S4 build-spec §6 tracks the gap).
export const MinPropertiesPurchasedOptions = [
  { value: '', label: 'Any' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
];

// PROVISIONAL — the PRD gives the range semantics ($250K–$400K example) but no
// dropdown steps, and the analysis page doesn't enumerate them either. These
// steps are a placeholder pending the real list (S4 build-spec §6).
export const AvgPurchasePriceOptions = [
  50000, 100000, 150000, 200000, 250000, 300000, 400000, 500000, 750000, 1000000,
].map((value) => ({
  value,
  // Abbreviated with its unit: $50K ... $1M — the shape the investor cards
  // already render through `formatCompactCurrency`. Written out here rather
  // than imported from investorUtils, because that module imports THIS one and
  // the cycle would leave one of the two half-initialised at load.
  //
  // History worth keeping: ATAI-181 stripped "$", "K" and "+" from these lists,
  // and ATAI-469 then reported "$50" where "$50,000" was meant — an
  // abbreviation that had shed its magnitude. The unit letter is what guards
  // against repeating that: "$50K" cannot be misread as fifty dollars the way a
  // bare "$50" was.
  label: value >= 1000000 ? `$${value / 1000000}M` : `$${value / 1000}K`,
}));

// Sort modes. Best Match is computed server-side over the whole capped set;
// the vendor sorts map to BatchData fields. The sort UI itself is a later slice
// of S4 — this list exists so the search request can carry a mode.
export const SortOptions = [
  { value: 'BEST_MATCH', label: 'Best Match' },
  { value: 'ACQUISITION_COUNT', label: 'Acquisition Count' },
  { value: 'AVG_ACQUISITION_PRICE', label: 'Avg Acquisition Price' },
  { value: 'SQUARE_FEET', label: 'Square Feet' },
  { value: 'DISTANCE', label: 'Distance' },
];

export const DefaultFilters = {
  radius: 2,
  timeWindow: 2,
  propertyType: 'ANY',
  investorTypes: [],
  avgPurchasePriceMin: '',
  avgPurchasePriceMax: '',
  minPropertiesPurchased: '',
  sort: 'BEST_MATCH',
};

// NOTE: the POC's working request sends NO `options.sort` at all, so neither do
// we — the vendor rejects unrecognised sort paths, and a rejected search can come
// back as an empty result rather than an error. The sort filter therefore has no
// effect on results yet; wiring it needs the allowed sort paths confirmed.

export default INVESTOR_SEARCH_PATH;
